import React, { useMemo, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../store";
import PaymentModal from "../common/PaymentModal";

import {
  useProjects,
  type Project as ApiProject,
} from "../../services/projectService";

// Transform API project to component project
const transformProject = (
  apiProject: ApiProject,
  t: (key: string, defaultValue?: string) => string,
  locale: string
): Project => {
  const translation =
    apiProject.translations?.find((trans) => trans.locale === locale) ||
    apiProject.translations?.[0];
  const image = apiProject.media?.[0]?.path || "/placeholder-project.jpg";

  return {
    id: apiProject.id.toString(),
    title: translation?.title || "Untitled Project",
    description: translation?.description || "",
    image,
    currentFunding: apiProject.raised_amount,
    targetFunding: apiProject.goal_amount,
    category: t("projects.categories.community", "Community"),
    location: t("projects.locations.djichami", "Djichami"),
    status: apiProject.status as
      | "planning"
      | "in_progress"
      | "completed"
      | "on_hold",
  };
};

interface Project {
  id: string;
  title: string;
  description: string;
  image: string;
  currentFunding: number;
  targetFunding: number;
  category?: string;
  location?: string;
  status?: "planning" | "in_progress" | "completed" | "on_hold";
}

interface LatestProjectsProps {
  title?: string;
  subtitle?: string;
  projects?: Project[];
  showViewAll?: boolean;
  onViewAll?: () => void;
  onProjectClick?: (project: Project) => void;
  onContribute?: (project: Project) => void;
}

const LatestProjects: React.FC<LatestProjectsProps> = ({
  title,
  subtitle,
  projects,
  showViewAll = true,
  onViewAll,
  onProjectClick,
  onContribute,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedProjectForPayment, setSelectedProjectForPayment] = useState<Project | null>(null);

  // Fetch projects from API
  const {
    data: projectsResponse,
    isLoading,
    error,
  } = useProjects({
    per_page: 6,
    locale: currentLanguage,
  });

  // Transform API projects to component format
  const apiProjects = useMemo(() => {
    if (!projectsResponse?.projects) return [];
    return projectsResponse.projects.map((apiProject) =>
      transformProject(
        apiProject,
        t as (key: string, defaultValue?: string) => string,
        currentLanguage
      )
    );
  }, [projectsResponse, t, currentLanguage]);

  const displayProjects = projects || apiProjects;

  useEffect(() => {
    if (!isLoading && displayProjects.length > 0) {
      setIsDataLoaded(true);
    }
  }, [isLoading, displayProjects.length]);

  const calculateProgress = (current: number, target: number) => {
    return Math.min((current / target) * 100, 100);
  };

  const handleContributeClick = (project: Project) => {
    if (onContribute) {
      onContribute(project);
    } else {
      setSelectedProjectForPayment(project);
      setIsPaymentModalOpen(true);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-stone-50/50 via-emerald-50/30 to-stone-50/50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white mb-6">
              {title || t("projects.title", "Latest Projects")}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              {subtitle ||
                t(
                  "projects.subtitle",
                  "Discover our ongoing initiatives making a difference in our community"
                )}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-10">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="relative">
                <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden h-full animate-pulse">
                  <div className="h-56 bg-gray-200 dark:bg-gray-700"></div>
                  <div className="p-6">
                    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-6"></div>
                    <div className="flex gap-3">
                      <div className="flex-1 h-10 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                      <div className="h-10 w-20 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-stone-50/50 via-emerald-50/30 to-stone-50/50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white mb-6">
            {title || t("projects.title", "Latest Projects")}
          </h2>
          <p className="text-xl text-red-600 dark:text-red-400">
            {t(
              "projects.error",
              "Failed to load projects. Please try again later."
            )}
          </p>
        </div>
      </section>
    );
  }

  // Empty state
  if (displayProjects.length === 0) {
    return (
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-stone-50/50 via-emerald-50/30 to-stone-50/50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white mb-6">
              {title || t("projects.title", "Latest Projects")}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              {subtitle ||
                t(
                  "projects.subtitle",
                  "Discover our ongoing initiatives making a difference in our community"
                )}
            </p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full mb-6">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {t("projects.noProjects", "No Projects Available")}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {t(
                "projects.noProjectsDescription",
                "There are currently no projects to display. Check back later for new initiatives."
              )}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-stone-50/50 via-emerald-50/30 to-stone-50/50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-stone-200/20 dark:bg-stone-900/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-200/20 dark:bg-emerald-900/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={
              isDataLoaded
                ? { scale: 1, opacity: 1 }
                : { scale: 0.9, opacity: 0 }
            }
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-block mb-6"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-stone-800 dark:text-stone-300 rounded-full text-sm font-semibold border border-stone-200 dark:border-stone-800 shadow-lg">
              <span className="text-lg">🚀</span>
              <span>{t("projects.badge", "Community Initiatives")}</span>
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={
              isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 dark:text-white mb-6 leading-tight tracking-tight"
          >
            {title || t("projects.title", "Latest Projects")}
            <span className="block mt-2 bg-gradient-to-r from-stone-600 via-emerald-600 to-stone-700 bg-clip-text text-transparent">
              {t("projects.titleAccent", "Making an Impact")}
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={
              isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto"
          >
            {subtitle ||
              t(
                "projects.subtitle",
                "Discover our ongoing initiatives making a difference in our community"
              )}
          </motion.p>
        </motion.div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-10">
          {displayProjects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 50 }}
              animate={
                isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }
              }
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              whileHover={{ y: -12, scale: 1.03 }}
              className="group relative"
            >
              {/* Floating gradient glow */}
              <div className="absolute -inset-1 bg-gradient-to-r from-stone-500 via-emerald-500 to-stone-600 rounded-3xl opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-500"></div>

              {/* Main Card */}
              <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 overflow-hidden h-full">
                {/* Image Section */}
                <div className="relative h-64 overflow-hidden">
                  <div
                    className="w-full h-full bg-center bg-no-repeat bg-cover transition-transform duration-500 group-hover:scale-110"
                    style={{ backgroundImage: `url('${project.image}')` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                  {/* Status Badge */}
                  <div className="absolute top-4 left-4">
                    <span
                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold backdrop-blur-md ${project.status === "completed"
                        ? "bg-emerald-500/90 text-white"
                        : project.status === "in_progress"
                          ? "bg-stone-500/90 text-white"
                          : project.status === "planning"
                            ? "bg-stone-500/90 text-white"
                            : "bg-stone-500/90 text-white" // on_hold
                        }`}
                    >
                      {t(
                        `projects.status.${project.status || "planning"}`,
                        project.status || "planning"
                      )}
                    </span>
                  </div>

                  {/* Category Badge */}
                  {project.category && (
                    <div className="absolute top-4 right-4">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-white/20 backdrop-blur-md text-white border border-white/30">
                        {project.category}
                      </span>
                    </div>
                  )}

                  {/* Floating stat card */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={
                      isDataLoaded
                        ? { opacity: 1, scale: 1 }
                        : { opacity: 0, scale: 0.8 }
                    }
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    className="absolute bottom-4 left-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl px-4 py-2 shadow-lg border border-white/50 dark:border-gray-700/50"
                  >
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {t("projects.funded", "Funded")}
                    </div>
                    <div className="text-lg font-bold text-emerald-600 dark:text-emerald-500">
                      {Math.round(
                        calculateProgress(
                          project.currentFunding,
                          project.targetFunding
                        )
                      )}
                      %
                    </div>
                  </motion.div>
                </div>

                {/* Content Section */}
                <div className="p-6 lg:p-8">
                  <h3 className="text-xl lg:text-2xl font-extrabold text-gray-900 dark:text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-stone-600 group-hover:to-emerald-600 transition-all duration-300 line-clamp-2">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm lg:text-base leading-relaxed mb-6 line-clamp-3">
                    {project.description}
                  </p>

                  {/* Funding Progress */}
                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {t("projects.fundingProgress", "Funding Progress")}
                      </span>
                      <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                        XAF {project.currentFunding.toLocaleString()} / XAF
                        {project.targetFunding.toLocaleString()}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={
                          isDataLoaded
                            ? {
                              width: `${calculateProgress(
                                project.currentFunding,
                                project.targetFunding
                              )}%`,
                            }
                            : { width: 0 }
                        }
                        viewport={{ once: true }}
                        transition={{
                          duration: 1.5,
                          delay: 0.6 + index * 0.1,
                          ease: "easeOut",
                        }}
                        className="bg-gradient-to-r from-stone-500 via-emerald-500 to-stone-600 h-full rounded-full relative overflow-hidden"
                      >
                        <div className="absolute inset-0 bg-white/30 rounded-full"></div>
                      </motion.div>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                        {Math.round(
                          calculateProgress(
                            project.currentFunding,
                            project.targetFunding
                          )
                        )}
                        % {t("projects.funded", "funded")}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                        XAF{" "}
                        {Math.max(
                          0,
                          project.targetFunding - project.currentFunding
                        ).toLocaleString()}{" "}
                        {t("projects.remaining", "remaining")}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        if (onProjectClick) {
                          onProjectClick(project);
                        } else {
                          navigate(
                            `/${currentLanguage}/projects/${project.id}`
                          );
                        }
                      }}
                      className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-xl font-semibold transition-all duration-200 text-sm"
                    >
                      {t("projects.viewDetails", "View Details")}
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleContributeClick(project)}
                      className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-stone-600 hover:from-emerald-700 hover:to-stone-700 text-white rounded-xl font-bold transition-all duration-200 text-sm shadow-lg hover:shadow-xl"
                    >
                      💚 {t("projects.contribute", "Contribute")}
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View All Button */}
        {showViewAll && displayProjects.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={
              isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }
            }
            transition={{ duration: 0.6, delay: 1 }}
            className="text-center mt-20"
          >
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                if (onViewAll) {
                  onViewAll();
                } else {
                  navigate(`/${currentLanguage}/projects`);
                }
              }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-800 text-gray-900 dark:text-white rounded-2xl font-bold text-lg transition-all duration-200 shadow-xl hover:shadow-2xl border-2 border-gray-200 dark:border-gray-700"
            >
              {t("projects.viewAll", "View All Projects")}
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </motion.button>
          </motion.div>
        )}
      </div>

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        projectTitle={selectedProjectForPayment?.title}
      />
    </section>
  );
};

export default LatestProjects;
